import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MappingData, ReturnData } from 'store/returns/models';
import { Col, Row } from 'antd';
import { MapTaxRates } from './map-tax-rates';
import { Section, TaxRate } from 'store/returns/models/returnData';
import { TifdActivityDraft } from 'store/returns/models/mappingTaxRate';
import { SidePanel } from './side-panel';
import {
  AiMappingLabels,
  AiMappingStepsOverlay,
} from './ai-mapping-steps-overlay';
import {
  ReverseCharge,
  SelectedRate,
  StepOneData,
  TaxRateOption,
} from 'pages/returns';
import { TaxRateType } from 'store/app/enums';
import {
  PreferenceSections,
  PreferenceActions,
  Preference,
} from 'utils/preferences';
import { AiMappingStatus } from 'store/returns/types';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { isMaltaTifdReturnType } from 'utils/tifd-mapping';
interface AddMappingFormProps {
  preLoading: boolean;
  taxRatesLoading: boolean;
  loading: boolean;
  returnData?: ReturnData;
  mappingData?: MappingData;
  stepOneData: StepOneData;
  form2: any;
  editMode: boolean;
  selectedRates: SelectedRate[];
  selectedRatesCopy: SelectedRate[];
  reverseCharges: ReverseCharge[];
  outOfScopeCode: string;
  cashAccounting: boolean;
  handleCashAccounting: (rateId: string) => void;
  handleReverseCharge: (rateId: string, value: string | null) => void;
  addUnmappedToOutOfScope: () => void;
  updateSelectedRates: (mappedData: SelectedRate[]) => void;
  sections?: Section[];
  taxRateOptions?: TaxRateOption[];
  outOfScopeOptions?: TaxRateOption[];
  handleTaxRangeChange: (
    rateIds: string[],
    outOfScope: boolean,
    code: string
  ) => void;
  setOptions: (trOptions: TaxRateOption[], oosOptions: TaxRateOption[]) => void;
  prepareTaxRateOptions: () => TaxRateOption[];
  hasTemplates: boolean;
  loadTemplateMappings: () => void;
  openTemplateWindow: () => void;
  openDefaultMappingsWindow: () => void;
  resetForm: () => void;
  editInitialData: () => void;
  saveCashAccounting: () => void;
  saveReverseCharges: () => boolean;
  resetReverseCharge: () => void;
  fillCashAccounting: (value: boolean) => void;
  loadCashAccounting: () => void;
  loadReverseCharge: () => void;
  onSubmit: () => void;
  updateTaxRateType: (type: TaxRateType, rateId: string | any[]) => void;
  defaultTaxRatesData?: any;
  handlePreferences: (
    preferenceSection: PreferenceSections,
    action: PreferenceActions,
    preference: Preference
  ) => any;
  providerName?: string;
  mapWithAI: () => void;
  aiMappingStatus: AiMappingStatus;
  onAiMappingDismiss: () => void;
  /** Bumps when Reset clears the mapping, so the account filter starts fresh. */
  ratesResetKey?: number;
  includeCashAndReverseCharge?: boolean;
  /** Malta TIFD sections show the title, then balance sheet or income. */
  showSectionSheet?: boolean;
  /** When set, dropdowns and the side list use this list instead of tax rates. */
  mappingRates?: TaxRate[];
  /** Side-list heading. Defaults to tax rate mapping. */
  ratesListTitle?: string;
  aiLabels?: AiMappingLabels;
  tifdActivities?: TifdActivityDraft[];
  onTifdActivitiesChange?: (activities: TifdActivityDraft[]) => void;
  onActivityWarning?: (block: 'required' | 'empty') => void;
  accountExtras?: Record<
    string,
    { creditBalanceCode: string | null; activityKey: string | null }
  >;
  onCreditBalanceCodeChange?: (accountId: string, code: string | null) => void;
  onAccountActivityChange?: (accountId: string, activityKey: string) => void;
}

export const mapTaxRatesStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface-raised, #ffffff)',
  padding: '40px 30px 0 40px',
};

export const MappingForm: React.FC<AddMappingFormProps> = ({
  preLoading,
  taxRatesLoading,
  loading,
  returnData,
  mappingData,
  stepOneData,
  form2,
  editMode,
  selectedRates,
  selectedRatesCopy,
  reverseCharges,
  outOfScopeCode,
  cashAccounting,
  handleCashAccounting,
  handleReverseCharge,
  addUnmappedToOutOfScope,
  updateSelectedRates,
  sections,
  taxRateOptions,
  outOfScopeOptions,
  handleTaxRangeChange,
  setOptions,
  prepareTaxRateOptions,
  hasTemplates,
  loadTemplateMappings,
  openTemplateWindow,
  openDefaultMappingsWindow,
  resetForm,
  editInitialData,
  saveCashAccounting,
  saveReverseCharges,
  resetReverseCharge,
  fillCashAccounting,
  loadCashAccounting,
  loadReverseCharge,
  onSubmit,
  updateTaxRateType,
  defaultTaxRatesData,
  handlePreferences,
  providerName,
  mapWithAI,
  aiMappingStatus,
  onAiMappingDismiss,
  includeCashAndReverseCharge = true,
  showSectionSheet = false,
  mappingRates,
  ratesListTitle,
  aiLabels,
  tifdActivities,
  onTifdActivitiesChange,
  onActivityWarning,
  ratesResetKey = 0,
  accountExtras,
  onCreditBalanceCodeChange,
  onAccountActivityChange,
}) => {
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const tifdReturn = isMaltaTifdReturnType(mappingData?.typeId);
  const cashAndReverseCharge = includeCashAndReverseCharge && !tifdReturn;
  const sectionSheet = showSectionSheet || tifdReturn;
  const inputOutput = !tifdReturn;
  const listRates = mappingRates ?? returnData?.taxRates;
  const listTitle =
    ratesListTitle ?? t('returns.mapping.side-panel.tax-rate-mapping');
  const [useCashAccounting, setUseCashAccounting] = useState<boolean>(false);
  const [useReverseCharge, setUseReverseCharge] = useState<boolean>(false);
  const toggleCA = (value: boolean) => setUseCashAccounting(value);
  const toggleRC = (value: boolean) => setUseReverseCharge(value);

  useEffect(() => {
    if (tifdReturn || loading) return;
    loadTemplateMappings();
  }, [editMode, loading, tifdReturn]);

  if (taxRatesLoading) return null;
  return (
    <div
      className={`mapping-form-root mapping-form-page${
        pwaMobileNav ? ' mapping-form-root--pwa' : ''
      }`}
      style={{ background: 'var(--color-surface, #f3f7f8)' }}
    >
      <Row
        justify={'space-between'}
        style={{ paddingRight: pwaMobileNav ? 0 : 450 }}
      >
        <Col
          span={24}
          style={{ background: 'var(--color-surface-raised, #ffffff)' }}
        >
          {!preLoading && (
            <>
              <div
                className={
                  pwaMobileNav
                    ? 'mapping-form-map-rates mapping-form-map-rates--pwa'
                    : 'mapping-form-map-rates'
                }
                style={pwaMobileNav ? undefined : mapTaxRatesStyle}
              >
                <MapTaxRates
                  sections={sections}
                  taxRateOptions={taxRateOptions}
                  outOfScopeOptions={outOfScopeOptions}
                  outOfScopeCode={outOfScopeCode}
                  handleTaxRangeChange={handleTaxRangeChange}
                  form={form2}
                  mappingData={mappingData}
                  taxRates={listRates}
                  updateSelectedRates={updateSelectedRates}
                  setOptions={setOptions}
                  prepareTaxRateOptions={prepareTaxRateOptions}
                  addUnmapped={addUnmappedToOutOfScope}
                  useCashReverse={useCashAccounting || useReverseCharge}
                  selectedRates={selectedRates}
                  updateTaxRateType={updateTaxRateType}
                  stepOneData={stepOneData}
                  editMode={editMode}
                  useCashAccounting={useCashAccounting}
                  editInitialData={editInitialData}
                  hasTemplates={hasTemplates}
                  openTemplateWindow={openTemplateWindow}
                  includeLoadFromEntity={!tifdReturn}
                  openDefaultMappingsWindow={openDefaultMappingsWindow}
                  ratesResetKey={ratesResetKey}
                  useReverseCharge={useReverseCharge}
                  defaultTaxRatesData={defaultTaxRatesData}
                  handlePreferences={handlePreferences}
                  mapWithAI={mapWithAI}
                  aiMappingLoading={aiMappingStatus === 'loading'}
                  includeCashAndReverseCharge={cashAndReverseCharge}
                  showSectionSheet={sectionSheet}
                  includeInputOutput={inputOutput}
                  tifdActivities={tifdActivities}
                  onTifdActivitiesChange={onTifdActivitiesChange}
                />
              </div>
            </>
          )}
        </Col>
      </Row>

      <AiMappingStepsOverlay
        status={aiMappingStatus}
        onDismiss={onAiMappingDismiss}
        dismissible
        labels={aiLabels}
      />

      <div
        className={
          pwaMobileNav
            ? 'mapping-form-side-panel mapping-form-side-panel--pwa'
            : 'mapping-form-side-panel'
        }
        style={
          pwaMobileNav
            ? undefined
            : {
                overflow: 'auto',
                position: 'fixed',
                top: 70,
                insetInlineEnd: 0,
                height: 'calc(100vh - 70px)',
                maxWidth: 450,
                width: 450,
                zIndex: 100,
              }
        }
        tabIndex={0}
        role="region"
        aria-label={listTitle}
      >
        <SidePanel
          selectedRates={selectedRates}
          selectedRatesCopy={selectedRatesCopy}
          reverseCharges={reverseCharges}
          taxRates={listRates}
          ratesListTitle={listTitle}
          outOfScopeCode={outOfScopeCode}
          handleCashAccounting={handleCashAccounting}
          handleReverseCharge={handleReverseCharge}
          hasCashAccounting={cashAccounting}
          saveCashAccounting={saveCashAccounting}
          saveReverseCharges={saveReverseCharges}
          fillCashAccounting={fillCashAccounting}
          resetReverseCharge={resetReverseCharge}
          loadCashAccounting={loadCashAccounting}
          loadReverseCharge={loadReverseCharge}
          useCashAccounting={useCashAccounting}
          toggleCA={toggleCA}
          useReverseCharge={useReverseCharge}
          toggleRC={toggleRC}
          updateTaxRateType={updateTaxRateType}
          onSubmit={onSubmit}
          resetForm={resetForm}
          useCashReverse={useCashAccounting || useReverseCharge}
          providerName={providerName}
          saving={loading}
          includeCashAndReverseCharge={cashAndReverseCharge}
          includeInputOutput={inputOutput}
          validateActivities={tifdReturn}
          hideIgnoredAccounts={tifdReturn}
          onActivityWarning={onActivityWarning}
          sections={tifdReturn ? sections : undefined}
          tifdActivities={tifdReturn ? tifdActivities : undefined}
          accountExtras={tifdReturn ? accountExtras : undefined}
          onCreditBalanceCodeChange={
            tifdReturn ? onCreditBalanceCodeChange : undefined
          }
          onAccountActivityChange={
            tifdReturn ? onAccountActivityChange : undefined
          }
        />
      </div>
    </div>
  );
};
